const extractNumericToken = (input: string): string | null => {
  const matches = input
    .replace(/[€$£¥%]/g, " ")
    .replace(/\u00a0/g, " ")
    .replace(/duendecillo[_\s-]*base/gi, " ")
    .replace(/duendecillo/gi, " ")
    .replace(/base\s+imponible/gi, " ")
    .match(/[-+]?\d(?:[\d.,\s]*\d)?/g);

  if (!matches || matches.length === 0) return null;

  return matches[0].replace(/\s+/g, "");
};

const normalizeNumericToken = (token: string) => {
  const hasComma = token.includes(",");
  const hasDot = token.includes(".");

  if (hasComma) {
    // Spanish convention: comma is the decimal separator; dots (if any) are thousands separators
    return token.replace(/\./g, "").replace(",", ".");
  }

  if (hasDot) {
    const parts = token.split(".");
    // Dot is treated as a thousands separator only when it is unambiguous:
    // multiple dots (1.234.567) or exactly 3 digits after a dot preceded by
    // 1-3 digits (1.234, 12.345). With 1-2 trailing digits ("12.34", "100.50",
    // "1234.56") the dot is a decimal point.
    const lastPart = parts[parts.length - 1];
    const prevPart = parts[parts.length - 2] ?? "";
    const isLikelyThousands =
      parts.length >= 3 ||
      (parts.length === 2 &&
        /^\d{3}$/.test(lastPart) &&
        /^\d{1,3}$/.test(prevPart));

    if (isLikelyThousands) {
      return token.replace(/\./g, "");
    }

    return token;
  }

  return token;
};

export const parseNumericLike = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value === "string") {
    const numericToken = extractNumericToken(value);
    if (!numericToken) return 0;

    const parsed = Number.parseFloat(normalizeNumericToken(numericToken));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

export const formatNumber = (
  value: unknown,
  options: { decimals?: number; minimumDecimals?: number } = {}
): string => {
  const n = parseNumericLike(value);
  if (!Number.isFinite(n)) return "—";
  const decimals = options.decimals ?? 2;
  const minimumDecimals = options.minimumDecimals ?? decimals;
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: minimumDecimals,
    maximumFractionDigits: decimals,
  }).format(n);
};

export const formatInteger = (value: unknown): string => {
  const n = parseNumericLike(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n);
};

export const formatMoney = (value: unknown, options?: { decimals?: number }): string => {
  const n = parseNumericLike(value);
  if (!Number.isFinite(n)) return "— €";
  const decimals = options?.decimals ?? 2;
  return `${new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n)} €`;
};

/** Alias for formatMoney to keep existing imports working. */
export const formatMoneyLike = formatMoney;

